/**
 * sortRecords (Unit 5 Phase 1) — the records-grid comparator.
 *
 * PURE. No React, no Dexie, no storage. Takes the host's records array and a
 * sanitized sort list and returns a NEW array; the input is never mutated,
 * because the host's `records` is the one-shot snapshot other paths still
 * index into (FieldZonePlaceholder:134-139) and an in-place .sort() would
 * reorder it under them.
 *
 * VALUES COME FROM record.working[key], NEVER record[key]. This is the single
 * easiest thing to get wrong in this zone: the row document carries identity
 * and bookkeeping at the top level (record_id, site_id, tool_id, dirty, media)
 * and every FIELD VALUE one level down in `working`. A comparator reading
 * record[key] would find `tool_id` and undefined for all 27 others — every
 * comparison equal, every sort a silent no-op that still looks applied. The
 * grid's own cellContent (FieldRecordsList.jsx) reads the same place.
 *
 * TEXT ORDER IS NATURAL, NOT LEXICAL, and pinned to one locale:
 * localeCompare(a, b, 'en', { numeric: true, sensitivity: 'base' }). Ported
 * from Bob's comparator at toolsight-inv-v11_11.html:9313-9315, which is where
 * `numeric: true` comes from — it segments digit runs and compares them as
 * numbers, so TOOL-2 sorts before TOOL-10 rather than after it. The explicit
 * 'en' is ours, not his (he passes undefined): it stops the collation varying
 * with a device's own locale, so the order is a property of the data and not
 * of the handset. See compareText below for the full reasoning.
 *
 * EMPTY SORTS LAST IN BOTH DIRECTIONS. null, undefined and '' are absence of
 * data, not a value: a blank CSPC is not "before A" and it is not "after Z".
 * The empty test therefore runs BEFORE the direction flip, so descending puts
 * the largest value first and STILL leaves the blanks at the bottom. Leading a
 * descending sort with a screenful of blank cells is the failure this avoids —
 * a rep who sorts by DIE/OP descending wants the highest DIE/OP, not the rows
 * that have none. `false` is NOT empty; a FALSE flag is data and sorts as one.
 *
 * ⚠ THIS IS A DELIBERATE DIVERGENCE FROM BOB AND MUST NOT BE CONVERGED. His
 * String(x ?? '') at :9313-9314 coerces a blank to '', which collates before
 * every real value — so his blanks lead an ascending sort. The natural text
 * order above was ported FROM him; this rule was not, and the two decisions
 * are independent.
 *
 * STABLE, and explicitly so. Array.prototype.sort has been stable since ES2019
 * and every target browser honours it, but stability here is a contract rather
 * than an inherited convenience: a decorated original index breaks every tie,
 * so rows equal on all levels keep Load order, and adding a second level can
 * only refine the first — never reshuffle the rows it already ordered.
 */

import { isEmpty, KIND_BY_KEY, TEXT_COLLATOR } from './fieldValueRules';

/**
 * Flag rank: 0 for false, 1 for true, so false sorts before true ascending.
 *
 * Tolerates the STRING echo as well as a real boolean. A row that has been
 * through a server round-trip can carry `true`, and one rendered from a raw
 * import can carry 'true' — the grid already normalises this on display with
 * String(value) (cellContent's flag chip, FieldRecordsList.jsx), and the
 * comparator has to agree with what the rep can see in the cell.
 */
function boolRank(value) {
  return String(value).toLowerCase() === 'true' ? 1 : 0;
}

/**
 * NATURAL, case-insensitive, EXPLICIT-LOCALE string order.
 *
 * Ported from Bob's comparator — `av.localeCompare(bv, undefined, {numeric:true})`
 * at toolsight-inv-v11_11.html:9313-9315. `numeric: true` is the whole point:
 * it segments digit runs and compares them as numbers, so TOOL-2 sorts before
 * TOOL-10. A plain codepoint compare puts TOOL-10 first, on exactly the field
 * a rep is most likely to reach for.
 *
 * TWO DELIBERATE DIVERGENCES from Bob's line:
 *
 *   'en' INSTEAD OF undefined. `undefined` resolves to the device's own
 *   locale, so two reps on two phones could see one site ordered two ways.
 *   Pinning the locale fixes the collation to one table and makes the order a
 *   property of the data rather than of the handset.
 *
 *   sensitivity: 'base' INSTEAD OF .toLowerCase(). Case folding is what the
 *   collator's own sensitivity level is for, and pre-folding then comparing
 *   is not free: it mangles the input the numeric segmentation runs over.
 *   Fold via the collator, never before it.
 *
 * String() coercion is required, not defensive: this branch also catches the
 * six no-kind read-only rows and a number field's unparseable fallback, so `a`
 * can arrive as a number or a boolean, and the collator compares strings.
 *
 * HOISTED COLLATOR, not String.prototype.localeCompare. The two are
 * semantically identical given the same locale and options — this is a
 * performance change and nothing else. localeCompare resolves and builds a
 * collator on EVERY call: a 841-row site (the largest in TEST) sorted on two
 * levels is on the order of 16k comparisons, so that is 16k collators
 * constructed and thrown away inside one Apply tap, on a phone. TEXT_COLLATOR
 * is built once at module scope and reused, which is the documented reason
 * Intl.Collator exists as a separate object from the String method.
 */
function compareText(a, b) {
  return TEXT_COLLATOR.compare(String(a), String(b));
}

/**
 * One field's comparison, by kind. Returns <0, 0 or >0 in ASCENDING order —
 * the caller owns the direction flip, so the empty-last rule can be applied
 * above it.
 */
function compareValues(a, b, kind) {
  if (kind === 'number') {
    const an = Number(a);
    const bn = Number(b);
    // A number field holding something unparseable (a legacy free-text
    // quantity like "2 sets") must not become NaN and silently compare equal
    // to everything. Fall back to text for that pair only, so the well-formed
    // rows keep their numeric order.
    if (Number.isFinite(an) && Number.isFinite(bn)) {
      if (an === bn) return 0;
      return an < bn ? -1 : 1;
    }
    return compareText(a, b);
  }
  if (kind === 'bool') {
    return boolRank(a) - boolRank(b);
  }
  // enum, text, and no-kind (the five read-only display rows) all compare as
  // text. enum values ARE strings from refdata — there is no ordinal behind
  // them to sort by, so natural alphabetical is the only honest order.
  return compareText(a, b);
}

/**
 * Apply sort levels to a records array.
 *
 * @param {Array} records  the host's records (never mutated)
 * @param {Array} sort     sanitized levels, [{ fieldKey, direction }, ...]
 * @returns {Array} a new ordered array — or `records` ITSELF, by reference,
 *   when there is nothing to do. That identity is load-bearing: the host wraps
 *   this in a useMemo, so an unsorted grid gets the exact array it already had
 *   and React re-renders nothing.
 */
export default function sortRecords(records, sort) {
  if (!Array.isArray(records)) return records;
  if (!Array.isArray(sort) || sort.length === 0) return records;

  // Defence in depth. The host's levels arrive sanitized from
  // useFieldSortState, but this module is exported and pure — a caller that
  // hands it junk should get Load order back, not a throw inside a comparator.
  const levels = sort.filter(
    (level) => level && typeof level.fieldKey === 'string',
  );
  if (levels.length === 0) return records;

  // Decorate → sort → undecorate. The index is the stability guarantee.
  const decorated = records.map((record, index) => ({ record, index }));

  decorated.sort((x, y) => {
    for (const level of levels) {
      const { fieldKey, direction } = level;
      const av = x.record.working ? x.record.working[fieldKey] : undefined;
      const bv = y.record.working ? y.record.working[fieldKey] : undefined;

      const aEmpty = isEmpty(av);
      const bEmpty = isEmpty(bv);
      // Both blank on this level: it decides nothing, fall through to the
      // next level rather than calling them tied outright.
      if (aEmpty && bEmpty) continue;
      // ONE blank: it loses, and the direction is deliberately NOT consulted.
      // This is the whole "empty last in both directions" rule, and it has to
      // sit above the flip below to work.
      if (aEmpty) return 1;
      if (bEmpty) return -1;

      const result = compareValues(av, bv, KIND_BY_KEY.get(fieldKey));
      if (result !== 0) return direction === 'desc' ? -result : result;
    }
    // Equal on every level → original Load order.
    return x.index - y.index;
  });

  return decorated.map((d) => d.record);
}
